import { OidcClient } from "@axa-fr/react-oidc";
import { AlertCircleIcon } from "lucide-react";
import { useRef, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/locales/i18n-provider";
import { dismissSessionExpiration, type SessionExpirationState } from "./session-expiration";
import { useSessionExpiration } from "./use-session-expiration";

function ExpiredSessionRecovery({ state }: { state: Exclude<SessionExpirationState, "active"> }) {
  const { t } = useI18n();
  const loginPending = useRef(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);

  async function signInAgain() {
    if (loginPending.current) {
      return;
    }

    loginPending.current = true;
    setIsSigningIn(true);
    setLoginFailed(false);
    try {
      const client = OidcClient.get();
      if (!client) {
        throw new Error("OIDC client is unavailable.");
      }

      const { pathname, search, hash } = window.location;
      await client.loginAsync(`${pathname}${search}${hash}`, { prompt: "login" });
    } catch {
      loginPending.current = false;
      setIsSigningIn(false);
      setLoginFailed(true);
    }
  }

  const signInLabel = t(isSigningIn ? "auth.sessionExpired.signingIn" : "auth.sessionExpired.signIn");

  if (state === "dismissed") {
    return (
      <div className="fixed inset-x-4 bottom-4 z-40 mx-auto max-w-lg">
        <Alert className="shadow-lg">
          <AlertCircleIcon />
          <AlertTitle>{t("auth.sessionExpired.title")}</AlertTitle>
          <AlertDescription className="flex flex-col gap-3">
            <p>{t("auth.sessionExpired.reminder")}</p>
            {loginFailed && <p role="alert">{t("auth.sessionExpired.failed")}</p>}
            <Button className="self-start" disabled={isSigningIn} onClick={() => void signInAgain()}>
              {signInLabel}
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <AlertDialog
      open
      onOpenChange={(open) => {
        if (!open && !loginPending.current) {
          dismissSessionExpiration();
        }
      }}
    >
      <AlertDialogContent
        onEscapeKeyDown={(event) => {
          if (loginPending.current) {
            event.preventDefault();
          }
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{t("auth.sessionExpired.title")}</AlertDialogTitle>
          <AlertDialogDescription>{t("auth.sessionExpired.detail")}</AlertDialogDescription>
        </AlertDialogHeader>
        {loginFailed && (
          <Alert variant="destructive">
            <AlertDescription>{t("auth.sessionExpired.failed")}</AlertDescription>
          </Alert>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSigningIn}>{t("auth.sessionExpired.dismiss")}</AlertDialogCancel>
          <AlertDialogAction
            disabled={isSigningIn}
            onClick={(event) => {
              event.preventDefault();
              void signInAgain();
            }}
          >
            {signInLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** 独立于受保护页面挂载，重新认证失败时仍能保留提示与重试入口。 */
export function SessionExpiredNotice() {
  const state = useSessionExpiration();
  return state === "active" ? null : <ExpiredSessionRecovery state={state} />;
}
